import React, { useState, useEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { playerAPI, authAPI } from '../services/api';
import i18n from '../i18n';
import {
  SKILL_RANGE_OPTIONS as SKILL_RANGES,
  exactLevelsForRange,
  cantonOptionLabel,
} from '../constants/playerSignup';
import SkillReferenceModal from '../components/playerSignup/SkillReferenceModal';
import {
  setIncompleteProfileLeaveMessage,
  clearIncompleteProfileLeaveMessage,
} from '../utils/incompleteProfileLeaveGuard';
import PlayerIdField from '../components/playerProfile/PlayerIdField';
import {
  PLAYER_UI_LOCALES,
  PLAYER_UI_LOCALE_LABEL_KEYS,
  isPlayerUiLocale,
} from '../utils/playerUiLanguage';
import { formatDateTimeSwiss } from '../utils/swissDateTime';

const BRAND = { blue: '#043663', red: '#D52B1E', gray: '#E5E7EB', lightGray: '#f3f4f6' };

function getRangeForLevel(level) {
  if (level == null || level === '') return '';
  const n = parseFloat(level);
  if (Number.isNaN(n)) return '';
  for (const r of SKILL_RANGES) {
    const [min, max] = r.value.split('-').map(Number);
    if (n >= min && n <= max) return r.value;
  }
  return '';
}

const KIT_SIZE_OPTIONS = ['XXL', 'XL', 'L', 'M', 'S'];

const Card = styled.div`
  background: #fff;
  border-radius: 12px;
  padding: 1.75rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  border: 1px solid #e5e7eb;
  margin-bottom: 1.5rem;
`;

const SectionHeading = styled.h2`
  font-size: 1rem;
  font-weight: 600;
  color: #111827;
  margin: 0 0 1rem 0;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid ${BRAND.gray};
`;

const Label = styled.label`
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  margin-bottom: 0.375rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.625rem 0.875rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.9375rem;
  margin-bottom: 1rem;
  transition: border-color 0.15s, box-shadow 0.15s;

  &:focus {
    outline: none;
    border-color: ${BRAND.blue};
    box-shadow: 0 0 0 3px rgba(4, 54, 99, 0.12);
  }
  &:disabled {
    background: ${BRAND.lightGray};
    color: #6b7280;
    cursor: not-allowed;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.625rem 0.875rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.9375rem;
  margin-bottom: 1rem;
  background: #fff;
  transition: border-color 0.15s, box-shadow 0.15s;

  &:focus {
    outline: none;
    border-color: ${BRAND.blue};
    box-shadow: 0 0 0 3px rgba(4, 54, 99, 0.12);
  }
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const BtnPrimary = styled.button`
  padding: 0.625rem 1.5rem;
  background: ${BRAND.blue};
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 0.9375rem;
  font-weight: 600;
  cursor: pointer;
  margin-top: 0.5rem;
  transition: background 0.15s;

  &:hover {
    background: #032a4a;
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Hint = styled.p`
  font-size: 0.8125rem;
  color: #6b7280;
  margin: -0.25rem 0 1rem 0;
`;

const SkillLabelRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem 0.75rem;
  margin-bottom: 0.5rem;
`;

const SkillNotice = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.28rem 0.65rem;
  border-radius: 999px;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  color: ${BRAND.blue};
  font-size: 0.75rem;
  font-weight: 700;
  line-height: 1.3;
`;

const BtnSkillInfo = styled.button`
  width: 100%;
  padding: 0.65rem 1rem;
  margin: 0 0 1rem 0;
  background: #f8fafc;
  color: ${BRAND.blue};
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  &:hover {
    background: #f1f5f9;
    border-color: ${BRAND.blue};
  }
`;

const ProfileImageSection = styled.div`
  margin-bottom: 1.5rem;
`;
const AvatarWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 1.25rem;
  flex-wrap: wrap;
`;
const AvatarImg = styled.img`
  width: 96px;
  height: 96px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid ${BRAND.gray};
`;
const AvatarPlaceholder = styled.div`
  width: 96px;
  height: 96px;
  border-radius: 50%;
  background: ${BRAND.lightGray};
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  font-size: 2rem;
  border: 2px solid ${BRAND.gray};
`;
const AvatarActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;
const BtnSecondary = styled.button`
  padding: 0.5rem 1rem;
  background: #fff;
  color: ${BRAND.blue};
  border: 1px solid ${BRAND.blue};
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  &:hover {
    background: rgba(4, 54, 99, 0.06);
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;
const BtnDanger = styled(BtnSecondary)`
  color: #dc2626;
  border-color: #dc2626;
  &:hover {
    background: rgba(220, 38, 38, 0.06);
  }
`;

const LoadingCard = styled(Card)`
  color: #6b7280;
  font-size: 0.9375rem;
`;

export default function PlayerProfilePage() {
  const queryClient = useQueryClient();
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [saving, setSaving] = useState(false);
  const [localeSaving, setLocaleSaving] = useState(false);
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    dateOfBirth: '',
    sex: '',
    streetAddress: '',
    postalCode: '',
    city: '',
    country: '',
    nationalityPrimary: '',
    nationalitySecondary: '',
    kitSize: '',
    kitName: '',
    homeClub: '',
    instagramHandle: '',
    homeCanton: '',
    dominantHand: '',
    preferredCourtSide: '',
    swissTennisPadelLicensed: '',
  });
  const [emailError, setEmailError] = useState('');
  const [skillRange, setSkillRange] = useState('');
  const [exactSkillLevel, setExactSkillLevel] = useState('');
  const [skillMappingModalOpen, setSkillMappingModalOpen] = useState(false);
  const [profilePictureUrl, setProfilePictureUrl] = useState(null);
  const [pictureActionInProgress, setPictureActionInProgress] = useState(false);
  const profilePictureInputRef = useRef(null);

  const { data: profileRes, isLoading } = useQuery(
    ['playerProfile'],
    () => playerAPI.getProfile(),
    { enabled: !!user?.playerId }
  );

  const { data: cantonOptsRes, isLoading: loadingCantons } = useQuery(
    ['playerSignupCantonOptions'],
    () => authAPI.getPlayerSignupCantonOptions(),
    { staleTime: 60 * 60 * 1000 }
  );

  const profile = profileRes?.data;
  const cantonOptions = useMemo(() => {
    const raw = cantonOptsRes?.data;
    return Array.isArray(raw) ? raw : [];
  }, [cantonOptsRes]);

  const homeCantonLockedLabel = useMemo(() => {
    const code = form.homeCanton;
    if (!code) return '—';
    const c = cantonOptions.find((x) => String(x.code) === String(code));
    if (!c) return code;
    return cantonOptionLabel(c);
  }, [form.homeCanton, cantonOptions]);

  const exactSkillOptions = useMemo(() => exactLevelsForRange(skillRange), [skillRange]);

  const profileUiLocaleValue = useMemo(() => {
    if (isPlayerUiLocale(profile?.uiLocale)) return profile.uiLocale;
    const fromI18n = (i18n.language || 'en').split('-')[0];
    return PLAYER_UI_LOCALES.includes(fromI18n) ? fromI18n : 'en';
  }, [profile?.uiLocale]);

  useEffect(() => {
    if (profile) {
      setForm({
        firstName: profile.firstName ?? '',
        lastName: profile.lastName ?? '',
        email: profile.email ?? '',
        phoneNumber: profile.phoneNumber ?? '',
        dateOfBirth: profile.dateOfBirth ?? '',
        sex: profile.sex ?? '',
        streetAddress: profile.streetAddress ?? '',
        postalCode: profile.postalCode ?? '',
        city: profile.city ?? '',
        country: profile.country ?? '',
        nationalityPrimary: profile.nationalityPrimary ?? '',
        nationalitySecondary: profile.nationalitySecondary ?? '',
        kitSize: profile.tShirtSize || profile.shortSize || '',
        kitName: profile.kitName ?? '',
        homeClub: profile.homeClub ?? '',
        instagramHandle: profile.instagramHandle ?? '',
        homeCanton: profile.homeCanton ?? '',
        dominantHand: profile.dominantHand ?? '',
        preferredCourtSide: profile.preferredCourtSide ?? '',
        swissTennisPadelLicensed:
          profile.swissTennisPadelLicensed === true
            ? 'yes'
            : profile.swissTennisPadelLicensed === false
              ? 'no'
              : '',
      });
      setProfilePictureUrl(profile.profilePictureUrl ?? null);
      const sl = profile.skillLevel != null ? Number(profile.skillLevel) : null;
      if (sl != null && !Number.isNaN(sl)) {
        const range = getRangeForLevel(sl);
        setSkillRange(range);
        setExactSkillLevel(sl.toFixed(1));
      } else {
        setSkillRange('');
        setExactSkillLevel('');
      }
    }
  }, [profile]);

  useEffect(() => {
    if (user?.profileComplete === false) {
      setIncompleteProfileLeaveMessage(t('phase2.playerProfile.leaveIncompleteConfirm'));
    } else {
      clearIncompleteProfileLeaveMessage();
    }
    return () => clearIncompleteProfileLeaveMessage();
  }, [user?.profileComplete, t]);

  useEffect(() => {
    if (user?.profileComplete !== false) return undefined;
    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [user?.profileComplete]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: name === 'kitName' ? String(value).slice(0, 12) : value,
    }));
  };

  const handleUiLocaleChange = async (e) => {
    const v = e.target.value;
    if (!profile || localeSaving || !isPlayerUiLocale(v)) return;
    setLocaleSaving(true);
    try {
      const res = await playerAPI.updateProfile({ uiLocale: v });
      if (res?.success === false) {
        toast.error(res?.message || t('phase2.playerProfile.toasts.updateFailed'));
        return;
      }
      await i18n.changeLanguage(v);
      queryClient.invalidateQueries(['playerProfile']);
      toast.success(t('phase2.playerProfile.toasts.profileUpdated'));
    } catch (err) {
      toast.error(err?.response?.data?.message || t('phase2.playerProfile.toasts.updateFailed'));
    } finally {
      setLocaleSaving(false);
    }
  };

  const handleProfilePictureUpload = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;
    setPictureActionInProgress(true);
    try {
      const res = await playerAPI.uploadProfilePicture(file);
      const url = res?.data?.profilePictureUrl ?? res?.profilePictureUrl;
      if (url != null) {
        setProfilePictureUrl(url);
        queryClient.invalidateQueries(['playerProfile']);
        toast.success(t('phase2.playerProfile.toasts.pictureUpdated'));
      } else {
        toast.error(res?.message || t('phase2.playerProfile.toasts.uploadFailed'));
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || t('phase2.playerProfile.toasts.uploadFailed'));
    } finally {
      setPictureActionInProgress(false);
      if (profilePictureInputRef.current) profilePictureInputRef.current.value = '';
    }
  };

  const handleRemoveProfilePicture = async () => {
    setPictureActionInProgress(true);
    try {
      const res = await playerAPI.removeProfilePicture();
      const isError = res && (res.success === false || res.error != null);
      if (!isError) {
        setProfilePictureUrl(null);
        queryClient.invalidateQueries(['playerProfile']);
        toast.success(t('phase2.playerProfile.toasts.pictureRemoved'));
      } else {
        toast.error(res?.message || t('phase2.playerProfile.toasts.removeFailed'));
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || t('phase2.playerProfile.toasts.removeFailed'));
    } finally {
      setPictureActionInProgress(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;
    const wasIncomplete = user?.profileComplete === false;
    if (!profile?.skillLevelLocked && (!skillRange || !exactSkillLevel)) {
      toast.error(t('phase2.playerProfile.toasts.selectSkillAndLevel'));
      return;
    }
    if (!profile?.homeCantonLocked && !String(form.homeCanton || '').trim()) {
      toast.error(t('phase2.playerProfile.toasts.selectHomeCanton'));
      return;
    }
    if (!String(form.phoneNumber || '').trim()) {
      toast.error(t('phase2.playerProfile.toasts.phoneRequired'));
      return;
    }
    if (!String(form.dateOfBirth || '').trim()) {
      toast.error(t('phase2.playerProfile.toasts.dobRequired'));
      return;
    }
    if (!String(form.sex || '').trim()) {
      toast.error(t('phase2.playerProfile.toasts.sexRequired'));
      return;
    }
    if (
      !String(form.streetAddress || '').trim() ||
      !String(form.postalCode || '').trim() ||
      !String(form.city || '').trim() ||
      !String(form.country || '').trim()
    ) {
      toast.error(t('phase2.playerProfile.toasts.addressIncomplete'));
      return;
    }
    if (!String(form.nationalityPrimary || '').trim()) {
      toast.error(t('phase2.playerProfile.toasts.nationalityRequired'));
      return;
    }
    if (!String(form.homeClub || '').trim()) {
      toast.error(t('phase2.playerProfile.toasts.homeClubRequired'));
      return;
    }
    if (!form.dominantHand) {
      toast.error(t('phase2.playerProfile.toasts.dominantHandRequired'));
      return;
    }
    if (!form.preferredCourtSide) {
      toast.error(t('phase2.playerProfile.toasts.preferredSideRequired'));
      return;
    }
    if (form.swissTennisPadelLicensed !== 'yes' && form.swissTennisPadelLicensed !== 'no') {
      toast.error(t('phase2.playerProfile.toasts.swissLicenseRequired'));
      return;
    }
    setSaving(true);
    const emailTrim = form.email.trim();
    if (!emailTrim || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTrim)) {
      setEmailError(t('phase2.playerProfile.toasts.invalidEmail'));
      setSaving(false);
      return;
    }
    setEmailError('');
    try {
      const payload = {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email: emailTrim.toLowerCase(),
        phoneNumber: form.phoneNumber.trim(),
        dateOfBirth: form.dateOfBirth.trim(),
        sex: form.sex.trim(),
        streetAddress: form.streetAddress.trim(),
        postalCode: form.postalCode.trim(),
        city: form.city.trim(),
        country: form.country.trim(),
        nationalityPrimary: form.nationalityPrimary.trim(),
        nationalitySecondary: form.nationalitySecondary.trim() || null,
        tShirtSize: form.kitSize.trim() || null,
        shortSize: form.kitSize.trim() || null,
        kitName: form.kitName.trim() || null,
        homeClub: form.homeClub.trim(),
        instagramHandle: form.instagramHandle.trim() || null,
        dominantHand: form.dominantHand,
        preferredCourtSide: form.preferredCourtSide,
        swissTennisPadelLicensed: form.swissTennisPadelLicensed === 'yes',
      };
      if (!profile?.skillLevelLocked && exactSkillLevel) {
        payload.skillLevel = parseFloat(exactSkillLevel) || null;
      }
      const hcTrim = String(form.homeCanton || '').trim();
      if (!profile?.homeCantonLocked && hcTrim) {
        payload.homeCanton = hcTrim;
      }
      const res = await playerAPI.updateProfile(payload);
      if (res?.data && typeof res.data === 'object' && res.data.firstName !== undefined) {
        const d = res.data;
        updateUser({
          firstName: d.firstName,
          lastName: d.lastName,
          ...(d.email != null && { email: d.email }),
        });
        queryClient.invalidateQueries(['playerProfile']);
        try {
          const vr = await authAPI.verifyToken();
          const nowComplete =
            vr?.success && vr.data?.type === 'player' && vr.data.profileComplete === true;
          if (nowComplete) {
            updateUser({ profileComplete: true });
          }
          if (wasIncomplete && nowComplete) {
            toast.success(t('phase2.playerProfile.toasts.profileNowComplete'));
            navigate('/player', { replace: true });
            return;
          }
        } catch {
          /* ignore */
        }
        toast.success(t('phase2.playerProfile.toasts.profileUpdated'));
      } else {
        toast.error(res?.data?.message || t('phase2.playerProfile.toasts.updateFailed'));
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || t('phase2.playerProfile.toasts.updateFailed'));
    } finally {
      setSaving(false);
    }
  };

  const skillDeadlineLabel = profile?.skillEditDeadline
    ? formatDateTimeSwiss(profile.skillEditDeadline)
    : '';
  const skillWindowOpen = profile?.skillEditWindowOpen === true;

  if (isLoading) {
    return (
      <LoadingCard>
        <p style={{ margin: 0 }}>{t('phase2.playerProfile.loading')}</p>
      </LoadingCard>
    );
  }

  const skillBand =
    skillRange && typeof skillRange === 'string' && skillRange.includes('-')
      ? { min: skillRange.split('-')[0], max: skillRange.split('-')[1] }
      : { min: '', max: '' };

  return (
    <Card>
      <form onSubmit={handleSubmit}>
        <SectionHeading>{t('phase2.playerProfile.sections.profilePhoto')}</SectionHeading>
        <ProfileImageSection>
          <AvatarWrap>
            {profilePictureUrl ? (
              <AvatarImg src={profilePictureUrl} alt={t('phase2.playerProfile.photo.avatarAlt')} />
            ) : (
              <AvatarPlaceholder>👤</AvatarPlaceholder>
            )}
            <AvatarActions>
              <input
                ref={profilePictureInputRef}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp,image/heic,image/heif,image/tiff"
                onChange={handleProfilePictureUpload}
                style={{ display: 'none' }}
              />
              <BtnSecondary
                type="button"
                disabled={pictureActionInProgress}
                onClick={() => profilePictureInputRef.current?.click()}
              >
                {profilePictureUrl ? t('phase2.playerProfile.photo.changePhoto') : t('phase2.playerProfile.photo.uploadPhoto')}
              </BtnSecondary>
              {profilePictureUrl && (
                <BtnDanger
                  type="button"
                  disabled={pictureActionInProgress}
                  onClick={handleRemoveProfilePicture}
                >
                  {t('phase2.playerProfile.photo.removePhoto')}
                </BtnDanger>
              )}
            </AvatarActions>
          </AvatarWrap>
          <Hint>{t('phase2.playerProfile.photo.formatsHint')}</Hint>
        </ProfileImageSection>

        <PlayerIdField playerId={profile?.id} />

        <SectionHeading>{t('phase2.playerProfile.labels.preferredLanguage')}</SectionHeading>
        <Hint>{t('phase2.playerProfile.hints.preferredLanguage')}</Hint>
        <Select
          value={profileUiLocaleValue}
          disabled={localeSaving || !profile}
          onChange={handleUiLocaleChange}
          aria-label={t('phase2.playerProfile.labels.preferredLanguage')}
          style={{ marginBottom: '1.25rem' }}
        >
          {PLAYER_UI_LOCALES.map((code) => (
            <option key={code} value={code} lang={code}>
              {t(`phase2.playerProfile.localeOptions.${PLAYER_UI_LOCALE_LABEL_KEYS[code]}`)}
            </option>
          ))}
        </Select>

        <SectionHeading>{t('phase2.playerProfile.sections.personalInfo')}</SectionHeading>
        <Row>
          <div>
            <Label>{t('phase2.playerProfile.labels.firstName')}</Label>
            <Input
              name="firstName"
              value={form.firstName}
              onChange={handleChange}
              required
              placeholder={t('phase2.playerProfile.placeholders.firstName')}
            />
          </div>
          <div>
            <Label>{t('phase2.playerProfile.labels.lastName')}</Label>
            <Input
              name="lastName"
              value={form.lastName}
              onChange={handleChange}
              required
              placeholder={t('phase2.playerProfile.placeholders.lastName')}
            />
          </div>
        </Row>
        <div>
          <Label>{t('phase2.playerProfile.labels.email')}</Label>
          <Input
            name="email"
            type="email"
            value={form.email}
            onChange={handleChange}
            required
            placeholder={t('phase2.playerProfile.placeholders.email')}
          />
          {emailError && <Hint style={{ color: '#dc2626' }}>{emailError}</Hint>}
          {!emailError && <Hint>{t('phase2.playerProfile.hints.emailLogin')}</Hint>}
        </div>
        <div>
          <Label>{t('phase2.playerProfile.labels.phoneNumber')}</Label>
          <Input
            name="phoneNumber"
            type="tel"
            value={form.phoneNumber}
            onChange={handleChange}
            required
            placeholder={t('phase2.playerProfile.placeholders.phone')}
          />
        </div>
        <Row>
          <div>
            <Label>{t('phase2.playerProfile.labels.dateOfBirth')}</Label>
            <Input
              name="dateOfBirth"
              type="date"
              value={form.dateOfBirth}
              onChange={handleChange}
              required
              placeholder={t('phase2.playerProfile.placeholders.dateOfBirth')}
            />
          </div>
          <div>
            <Label>{t('phase2.playerProfile.labels.sex')}</Label>
            <Select name="sex" value={form.sex} onChange={handleChange} required>
              <option value="">{t('phase2.playerProfile.sex.select')}</option>
              <option value="male">{t('phase2.playerProfile.sex.male')}</option>
              <option value="female">{t('phase2.playerProfile.sex.female')}</option>
              <option value="other">{t('phase2.playerProfile.sex.other')}</option>
            </Select>
          </div>
        </Row>

        <Label>{t('phase2.playerProfile.labels.streetAddress')}</Label>
        <Input
          name="streetAddress"
          value={form.streetAddress}
          onChange={handleChange}
          required
          placeholder={t('phase2.playerProfile.placeholders.streetAddress')}
        />
        <Row>
          <div>
            <Label>{t('phase2.playerProfile.labels.postalCode')}</Label>
            <Input
              name="postalCode"
              value={form.postalCode}
              onChange={handleChange}
              required
              placeholder={t('phase2.playerProfile.placeholders.postalCode')}
            />
          </div>
          <div>
            <Label>{t('phase2.playerProfile.labels.city')}</Label>
            <Input name="city" value={form.city} onChange={handleChange} required placeholder={t('phase2.playerProfile.placeholders.city')} />
          </div>
        </Row>
        <Label>{t('phase2.playerProfile.labels.country')}</Label>
        <Input
          name="country"
          value={form.country}
          onChange={handleChange}
          required
          placeholder={t('phase2.playerProfile.placeholders.country')}
        />
        <Row>
          <div>
            <Label>{t('phase2.playerProfile.labels.nationalityPrimary')}</Label>
            <Input
              name="nationalityPrimary"
              value={form.nationalityPrimary}
              onChange={handleChange}
              required
              placeholder={t('phase2.playerProfile.placeholders.nationality')}
            />
          </div>
          <div>
            <Label>{t('phase2.playerProfile.labels.nationalitySecondary')}</Label>
            <Input
              name="nationalitySecondary"
              value={form.nationalitySecondary}
              onChange={handleChange}
              placeholder={t('phase2.playerProfile.placeholders.nationalityOptional')}
            />
          </div>
        </Row>
        <Label>{t('phase2.playerProfile.labels.kitSize')}</Label>
        <Select name="kitSize" value={form.kitSize} onChange={handleChange}>
          <option value="">{t('phase2.playerProfile.placeholders.selectKitSize')}</option>
          {KIT_SIZE_OPTIONS.map((size) => (
            <option key={size} value={size}>
              {size}
            </option>
          ))}
        </Select>
        <Hint>{t('phase2.playerProfile.hints.kitSizes')}</Hint>
        <Label>{t('phase2.playerProfile.labels.kitName')}</Label>
        <Input
          name="kitName"
          value={form.kitName}
          onChange={handleChange}
          maxLength={12}
          autoComplete="off"
          placeholder={t('phase2.playerProfile.placeholders.kitName')}
        />
        <Hint>{t('phase2.playerProfile.hints.kitNameMax')}</Hint>

        <SectionHeading>{t('phase2.playerProfile.sections.homeCanton')}</SectionHeading>
        {profile?.homeCantonLocked ? (
          <>
            <Label>{t('phase2.playerProfile.labels.homeCantonLocked')}</Label>
            <Input disabled value={homeCantonLockedLabel} readOnly />
            <Hint>{t('phase2.playerProfile.hints.homeCantonLocked')}</Hint>
          </>
        ) : (
          <>
            <Label>{t('phase2.playerProfile.labels.homeCantonRequired')}</Label>
            <Select
              name="homeCanton"
              value={form.homeCanton}
              onChange={handleChange}
              required
              disabled={loadingCantons || cantonOptions.length === 0}
            >
              <option value="">{t('phase2.playerProfile.placeholders.selectCanton')}</option>
              {cantonOptions.map((c) => {
                const code = c.code != null ? String(c.code) : '';
                if (!code) return null;
                return (
                  <option key={code} value={code}>
                    {cantonOptionLabel(c)}
                  </option>
                );
              })}
            </Select>
            <Hint>{t('phase2.playerProfile.hints.homeCantonOnce')}</Hint>
          </>
        )}

        <SectionHeading>{t('phase2.playerProfile.sections.playingDetails')}</SectionHeading>
        <div>
          <Label>{t('phase2.playerProfile.labels.homeClubNearest')}</Label>
          <Input
            name="homeClub"
            value={form.homeClub}
            onChange={handleChange}
            required
            placeholder={t('phase2.playerProfile.placeholders.homeClubNearest')}
          />
        </div>
        <Row>
          <div>
            <Label>{t('phase2.playerProfile.labels.dominantHand')}</Label>
            <Select
              name="dominantHand"
              value={form.dominantHand}
              onChange={handleChange}
              required
            >
              <option value="">{t('phase2.playerProfile.placeholders.selectHand')}</option>
              <option value="left">{t('phase2.playerProfile.hand.left')}</option>
              <option value="right">{t('phase2.playerProfile.hand.right')}</option>
            </Select>
          </div>
          <div>
            <Label>{t('phase2.playerProfile.labels.preferredCourtSide')}</Label>
            <Select
              name="preferredCourtSide"
              value={form.preferredCourtSide}
              onChange={handleChange}
              required
            >
              <option value="">{t('phase2.playerProfile.placeholders.selectSide')}</option>
              <option value="left">{t('phase2.playerProfile.side.left')}</option>
              <option value="right">{t('phase2.playerProfile.side.right')}</option>
              <option value="both">{t('phase2.playerProfile.side.both')}</option>
            </Select>
          </div>
        </Row>
        <div>
          <Label>{t('phase2.playerProfile.labels.swissTennisPadelLicensed')}</Label>
          <Select
            name="swissTennisPadelLicensed"
            value={form.swissTennisPadelLicensed}
            onChange={handleChange}
            required
          >
            <option value="">{t('phase2.playerProfile.placeholders.selectYesNo')}</option>
            <option value="yes">{t('phase2.playerProfile.yesNo.yes')}</option>
            <option value="no">{t('phase2.playerProfile.yesNo.no')}</option>
          </Select>
        </div>
        <div>
          <SkillLabelRow>
            <Label style={{ marginBottom: 0 }}>
              {t('phase2.playerProfile.labels.profileSkillLevel')}{' '}
              {!profile?.skillLevelLocked
                ? t('phase2.playerProfile.skill.required')
                : t('phase2.playerProfile.skill.locked')}
            </Label>
            {skillWindowOpen ? (
              <SkillNotice>
                {skillDeadlineLabel
                  ? t('phase2.playerProfile.skill.canChangeUntil', { deadline: skillDeadlineLabel })
                  : t('phase2.playerProfile.skill.canChangeDuringWindow')}
              </SkillNotice>
            ) : null}
          </SkillLabelRow>
          {!profile?.skillLevelLocked ? (
            <Hint>
              {skillWindowOpen
                ? skillDeadlineLabel
                  ? t('phase2.playerProfile.hints.skillUntilDeadline', { deadline: skillDeadlineLabel })
                  : t('phase2.playerProfile.hints.skillDuringWindow')
                : t('phase2.playerProfile.hints.skillOncePlaytomicSwiss')}
            </Hint>
          ) : null}
          <Hint>{t('playerRegister.skillReferenceHint')}</Hint>
          <BtnSkillInfo type="button" onClick={() => setSkillMappingModalOpen(true)}>
            {t('playerRegister.skillMappingMoreInfo')}
          </BtnSkillInfo>
          {profile?.skillLevelLocked ? (
            <>
              <Input
                disabled
                value={(() => {
                  const num =
                    exactSkillLevel ||
                    (profile?.skillLevel != null ? Number(profile.skillLevel).toFixed(1) : '');
                  if (!num) return '—';
                  const rLab =
                    skillRange && SKILL_RANGES.find((x) => x.value === skillRange)?.label;
                  return rLab ? `${num} (${rLab})` : String(num);
                })()}
                readOnly
              />
              <Hint style={{ whiteSpace: 'pre-line' }}>{t('phase2.playerProfile.hints.skillLockedApp')}</Hint>
            </>
          ) : (
            <>
              <Select
                value={skillRange}
                onChange={(e) => {
                  setSkillRange(e.target.value);
                  setExactSkillLevel('');
                }}
                required
              >
                <option value="">{t('phase2.playerProfile.placeholders.selectSkillRange')}</option>
                {SKILL_RANGES.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </Select>
              {skillRange && (
                <>
                  <Label>{t('phase2.playerProfile.labels.exactSkillLevel')}</Label>
                  <Select
                    value={exactSkillLevel}
                    onChange={(e) => setExactSkillLevel(e.target.value)}
                    required
                  >
                    <option value="">{t('phase2.playerProfile.placeholders.selectExactLevel')}</option>
                    {exactSkillOptions.map((v) => (
                      <option key={v} value={v}>
                        {v}
                      </option>
                    ))}
                  </Select>
                  <Hint>
                    {t('phase2.playerProfile.hints.exactLevelBand', {
                      min: skillBand.min,
                      max: skillBand.max,
                    })}
                  </Hint>
                </>
              )}
            </>
          )}
        </div>
        <div>
          <Label>
            {t('phase2.playerProfile.labels.instagramHandle')}{' '}
            <span style={{ fontWeight: 400, color: '#6b7280' }}>({t('phase2.playerProfile.labels.optional')})</span>
          </Label>
          <Input
            name="instagramHandle"
            value={form.instagramHandle}
            onChange={handleChange}
            placeholder={t('phase2.playerProfile.placeholders.instagram')}
          />
        </div>

        <BtnPrimary
          type="submit"
          disabled={
            saving || (!profile?.homeCantonLocked && (loadingCantons || cantonOptions.length === 0))
          }
        >
          {saving ? t('phase2.playerProfile.buttons.saving') : t('phase2.playerProfile.buttons.saveProfile')}
        </BtnPrimary>
      </form>
      <SkillReferenceModal
        open={skillMappingModalOpen}
        onClose={() => setSkillMappingModalOpen(false)}
      />
    </Card>
  );
}
